/**
 * LRC transcript formatting helpers.
 *
 * Mirrors the behaviour of `alux-lrc-tool/generate_lrc.py`:
 *  - `format_lrc_timestamp` -> `formatLrcTimestamp`
 *  - `correct_spelling` (incl. the "Aluxer" misheard variants) -> `correctSpelling`
 *  - `segments_to_lrc` -> `chunksToLrc`
 */

export interface TranscriptChunk {
  timestamp: [number, number | null];
  text: string;
}

/**
 * Formats seconds as an LRC timestamp tag, e.g. `12.345` -> `[00:12.35]`.
 * Matches the Python implementation: `minutes = int(seconds // 60)`, seconds
 * are zero-padded to 2 integer digits with 2 decimal places.
 */
export const formatLrcTimestamp = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const minutesStr = String(minutes).padStart(2, '0');
  const secsStr = secs.toFixed(2).padStart(5, '0');
  return `[${minutesStr}:${secsStr}]`;
};

// Misheard/misspelled variants Whisper produces for "Aluxer", normalized to it.
// Kept identical to the Python tool's `_ALUXER_VARIANTS` list.
const ALUXER_VARIANTS = [
  'Alexa',
  'alexa',
  'aLuxers',
  'ALuxers',
  'ALexa',
  'aLexa',
  'Luxur',
  'Luxers',
  'A-Luxers',
  'A-luxers',
  'relaxers',
  'A-Lux',
  'A-lux',
];

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whole words only: without the boundaries, variants matched inside longer
// words ("Alexander" -> "Aluxernder", "Luxury" -> "Aluxery").
const ALUXER_PATTERN = new RegExp(`\\b(?:${ALUXER_VARIANTS.map(escapeRegExp).join('|')})\\b`, 'g');

/** Replaces all known misheard "Aluxer" variants (whole words) with the correct spelling. */
export const correctSpelling = (text: string): string => text.replace(ALUXER_PATTERN, 'Aluxer');

/**
 * Converts ASR output chunks (as returned by the transformers.js
 * automatic-speech-recognition pipeline with `return_timestamps: true`) into
 * an LRC transcript string. Uses each chunk's start timestamp, applies
 * `correctSpelling` to the text, and strips lines that end up empty.
 */
export const chunksToLrc = (chunks: TranscriptChunk[]): string => {
  const lines: string[] = [];
  for (const chunk of chunks) {
    const [start] = chunk.timestamp;
    const timestamp = formatLrcTimestamp(start ?? 0);
    const text = correctSpelling((chunk.text ?? '').trim());
    if (text) {
      lines.push(`${timestamp}${text}`);
    }
  }
  return lines.join('\n');
};

/**
 * Applies `correctSpelling` to every line of an existing LRC string.
 * Used both before saving a transcript and before displaying one, so that
 * spelling fixes added after a transcript was generated still get applied.
 */
export const normalizeLrc = (lrc: string): string =>
  lrc
    .split('\n')
    .map((line) => correctSpelling(line))
    .join('\n');
